import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  Output,
  Renderer2,
  booleanAttribute,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';

import type { ButtonEdge, ButtonVariant } from './button.types';

@Directive({
  selector: '[uiBtn],[uiIconBtn]',
  standalone: true,
  host: {
    role: 'button',
    uiBtn: '',
    '[attr.type]': 'btnType()',
    '[attr.tabindex]': 'tabIndex()',
    '[attr.data-variant]': 'variant()',
    '[attr.data-size]': 'mini() ? "mini" : null',
    '[attr.data-edge]': 'edge()',
    '[attr.data-icon-only]': 'uiIconBtn() ? true : null',
    '[attr.aria-busy]': 'isLoading() ? true : null',
    '[attr.disabled]': '(isLoading() || disabled()) ? true : null',
  },
})
export class UiBtnDirective implements OnDestroy {
  public primary = input(false, { transform: booleanAttribute });
  public secondary = input(false, { transform: booleanAttribute });
  public tertiary = input(false, { transform: booleanAttribute });
  public inline = input(false, { transform: booleanAttribute });
  public outlined = input(false, { transform: booleanAttribute });
  public destructive = input(false, { transform: booleanAttribute });
  public ai = input(false, { transform: booleanAttribute });
  public btnType = input<'button' | 'submit' | 'reset'>('button');
  public tabIndex = input<number | string | null>(null);
  public throttleTime = input(500);
  public loading = input(false);
  public disabled = input(false, { transform: booleanAttribute });
  public mini = input(false, { transform: booleanAttribute });
  public uiIconBtn = input(false, { transform: booleanAttribute });
  public edge = input<ButtonEdge | null>(null);

  protected variant = computed<ButtonVariant>(() => {
    if (this.uiIconBtn()) {
      return this.iconVariant();
    }

    // Outlined has no destructive/ai counterpart in the design system, so it
    // wins outright rather than combining with those modifiers.
    if (this.outlined()) {
      return 'outlined';
    }

    const type = this.inline()
      ? 'inline'
      : this.tertiary()
        ? 'tertiary'
        : this.secondary()
          ? 'secondary'
          : 'primary';

    // destructive takes precedence over ai when both are set
    if (this.destructive()) {
      return type === 'primary' ? 'destructive' : `destructive-${type}`;
    }
    if (this.ai()) {
      return type === 'primary' ? 'ai' : `ai-${type}`;
    }
    return type;
  });

  // Icon buttons have only two types in the design system: a primary that
  // reads as tertiary, and a neutral secondary. Filled and outlined don't apply.
  // Inline keeps the icon button's box and drops the hover background.
  private iconVariant(): ButtonVariant {
    const [withBackground, inline]: [ButtonVariant, ButtonVariant] =
      this.destructive()
        ? ['destructive-tertiary', 'destructive-inline']
        : this.ai()
          ? ['ai-tertiary', 'ai-inline']
          : this.secondary()
            ? ['muted', 'muted-inline']
            : ['tertiary', 'inline'];
    return this.inline() ? inline : withBackground;
  }

  // EventEmitter is retained over output() because OutputEmitterRef lacks .observed,
  // which is needed to gate throttle behaviour on whether the output is bound.
  @Output() public throttleClick = new EventEmitter<MouseEvent>();

  private throttling = signal(false);
  protected isLoading = computed(() => this.loading() || this.throttling());

  private throttleTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly renderer = inject(Renderer2);
  private readonly el = inject(ElementRef);
  private loadingIcon: HTMLElement | null = null;

  constructor() {
    effect(() => {
      if (this.loading() && !this.loadingIcon) {
        this.loadingIcon = this.renderer.createElement('i');
        this.renderer.addClass(this.loadingIcon, 'icon-repeat');
        this.renderer.insertBefore(
          this.el.nativeElement,
          this.loadingIcon,
          this.el.nativeElement.firstChild
        );
      } else if (!this.loading() && this.loadingIcon) {
        this.renderer.removeChild(this.el.nativeElement, this.loadingIcon);
        this.loadingIcon = null;
      }
    });
  }

  public ngOnDestroy(): void {
    if (this.throttleTimer) clearTimeout(this.throttleTimer);
  }

  @HostListener('click', ['$event'])
  public clickEvent(event: MouseEvent): void {
    if (!this.throttleClick.observed) return;
    if (this.throttling()) return;

    event.preventDefault();
    this.throttling.set(true);
    this.throttleClick.emit(event);

    this.throttleTimer = setTimeout(() => {
      this.throttling.set(false);
    }, this.throttleTime());
  }

  @HostListener('mousedown', ['$event'])
  public mousedownEvent(event: MouseEvent): void {
    event.preventDefault();
  }
}
